export type PublicPageviewTrigger = 'dwell_3s' | 'interaction' | 'identified';

const INTERACTION_EVENTS = ['click', 'wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

// Match named crawlers, rather than fragments such as "bot" or "yandex" that
// also occur in real device and browser names (for example Cubot and YaBrowser).
const AUTOMATED_USER_AGENT =
  /(?:^|[^a-z0-9])(?:googlebot(?:-[a-z]+)?|google-inspectiontool|bingbot|bingpreview|gptbot|chatgpt-user|oai-searchbot|claudebot|claude-user|claude-searchbot|anthropic-ai|bytespider|yandexbot|yandeximages|yandexmetrika|duckduckbot|baiduspider|applebot|petalbot|semrushbot(?:-[a-z]+)?|ahrefsbot|ahrefssiteaudit|mj12bot|dotbot|slurp|headlesschrome|chrome-lighthouse|lighthouse|pingdom|datadog(?:synthetics)?|uptimerobot|uptimebot|facebookexternalhit|facebot|twitterbot|linkedinbot|slackbot(?:-linkexpanding)?|discordbot|telegrambot|skypeuripreview|embedly|pinterestbot)(?:[^a-z0-9]|$)/i;

export const isAutomatedTraffic = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;

  return (
    navigator.webdriver === true ||
    AUTOMATED_USER_AGENT.test(navigator.userAgent || '') ||
    new URLSearchParams(window.location.search).get('__test_runner') === 'true'
  );
};

export const isCoreAppRoute = (pathname: string): boolean =>
  ['/app', '/dashboard', '/analyze'].some((route) => pathname === route || pathname.startsWith(`${route}/`));

/**
 * Release one public pageview after visible dwell or a trusted interaction.
 * The caller owns route-change cancellation; the URL check also prevents an old
 * timer from attributing a new page's interaction to the previous page.
 */
export const createPublicPageviewGate = (send: (trigger: PublicPageviewTrigger) => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { cancel: () => undefined, flush: () => undefined };
  }

  const pageLocation = window.location.pathname + window.location.search;
  let state: 'active' | 'suspended' | 'closed' = 'active';
  let visibleDwellMs = 0;
  let visibleSince: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const stopVisibleDwell = () => {
    clearTimer();
    if (visibleSince !== null) visibleDwellMs += Math.max(0, Date.now() - visibleSince);
    visibleSince = null;
  };

  const removeActiveListeners = () => {
    INTERACTION_EVENTS.forEach((name) => window.removeEventListener(name, handleInteraction, true));
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
  };

  const cancel = () => {
    if (state === 'closed') return;
    state = 'closed';
    stopVisibleDwell();
    removeActiveListeners();
    window.removeEventListener('pageshow', handlePageShow);
  };

  const fire = (trigger: PublicPageviewTrigger) => {
    if (state === 'closed') return;
    const isCurrentPage = pageLocation === window.location.pathname + window.location.search;
    cancel();
    if (isCurrentPage) send(trigger);
  };

  function handleInteraction(event: Event) {
    if (state === 'active' && event.isTrusted && document.visibilityState === 'visible') fire('interaction');
  }

  function handleVisibilityChange() {
    if (state !== 'active') return;
    stopVisibleDwell();
    if (document.visibilityState !== 'visible') return;

    visibleSince = Date.now();
    timer = setTimeout(() => {
      if (state !== 'active') return;
      // A background tab can delay timers. Never release a dwell event while hidden.
      if (document.visibilityState !== 'visible') {
        handleVisibilityChange();
        return;
      }
      fire('dwell_3s');
    }, Math.max(0, 3000 - visibleDwellMs));
  }

  function handlePageHide(event: PageTransitionEvent) {
    if (state !== 'active') return;
    if (!event.persisted) {
      cancel();
      return;
    }

    // A cached document can return without mounting the app again. Retain only
    // the restoration listener and the visible time already earned.
    state = 'suspended';
    stopVisibleDwell();
    removeActiveListeners();
    window.addEventListener('pageshow', handlePageShow);
  }

  function handlePageShow(event: PageTransitionEvent) {
    if (state !== 'suspended' || !event.persisted) return;
    if (pageLocation !== window.location.pathname + window.location.search) {
      cancel();
      return;
    }

    window.removeEventListener('pageshow', handlePageShow);
    state = 'active';
    start();
  }

  function start() {
    // Scroll restoration and anchor jumps emit trusted scroll events without
    // user input. Listen to the inputs themselves so those visits still wait.
    INTERACTION_EVENTS.forEach((name) =>
      window.addEventListener(name, handleInteraction, { capture: true, passive: true })
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    handleVisibilityChange();
  }

  start();

  return { cancel, flush: () => fire('identified') };
};
